import { useState } from 'react';

import { defaultLocalStorageAdapter } from 'src/shared/graphql';
import { useAuthenticateWithCookieMutation } from 'src/shared/graphql/generated';
import { useRefreshSession } from 'src/shared/graphql/hooks';
import { logError } from 'src/shared/telemetry/logger';

import { requestTokenFromPartitionedLocalStorage } from '../utils/requestTokenFromPartitionedLocalStorage';
import { requestTokenFromPopup } from '../utils/requestTokenFromPopup';

export const useLoginWithTopLevelOriginToken = () => {
  const [authenticationState, setAuthenticationState] = useState({
    topLevelAccessed: false,
    authenticated: false,
  });
  const [authenticateWithCookie] = useAuthenticateWithCookieMutation();
  const refreshSession = useRefreshSession();
  const setIsUnauthenticated = () => {
    setAuthenticationState({
      topLevelAccessed: true,
      authenticated: false,
    });
  };

  const authenticateAndRefreshSession = async (token: string) => {
    try {
      const response = await authenticateWithCookie({
        context: {
          headers: {
            'X-JWT-AUTH': `Bearer ${token}`,
          },
        },
      });

      const { accessToken, refreshToken } =
        response?.data?.authenticateWithCookie?.jwt || {};

      if (accessToken && refreshToken) {
        await defaultLocalStorageAdapter.setAccessToken(accessToken);
        await defaultLocalStorageAdapter.setRefreshToken(refreshToken);
        await refreshSession();
        setAuthenticationState({
          topLevelAccessed: true,
          authenticated: true,
        });
      }
    } catch (error) {
      logError(error as Error);
      setAuthenticationState({
        topLevelAccessed: true,
        authenticated: false,
      });
    }
  };

  const authenticateWithTopLevelOriginToken = async () => {
    const response = await requestTokenFromPartitionedLocalStorage();

    if (response.localStorageAccessed && !response.accessToken) {
      setIsUnauthenticated();
      return;
    }

    if (response.accessToken) {
      await authenticateAndRefreshSession(response.accessToken);
      return;
    }

    void requestTokenFromPopup(
      authenticateAndRefreshSession,
      setIsUnauthenticated,
    );
  };

  return {
    authenticationState,
    authenticateWithTopLevelOriginToken,
  };
};
